import create from 'zustand';
import { postBondBuy, postBondSell } from 'global/Securities/api/bond';
import { BondTransactionActionEnum } from 'global/Securities/types/api/bond';

import { useCashOnHandStore } from './useCashOnHandStore';
import { useBondPreviewStore } from './useBondPreview';

interface State {
  transactionType: BondTransactionActionEnum;
  productId: string;
  unit: number;
  acquisitionOrderId: string;
  priceRate: number;
  isLoading: boolean;
  isTnCAccepted: boolean;
  isSuccess: boolean;
}

interface Action {
  submitOrder(): Promise<void>;
  clearData(): void;
  setTnC(value: boolean): void;
  initiateBuyTransaction(productId: string): Promise<void>;
  initiateSellTransaction(productId: string, orderId: string): Promise<void>;
}

type Store = State & Action;

const initialState: State = {
  transactionType: BondTransactionActionEnum.UNSPECIFIED,
  productId: '',
  unit: 0,
  acquisitionOrderId: '',
  priceRate: 0,
  isLoading: false,
  isTnCAccepted: false,
  isSuccess: false,
};

export const useBondTransaction = create<Store>((set, get) => ({
  ...initialState,

  async submitOrder() {
    try {
      set({ isLoading: true });
      const { isTnCAccepted, transactionType, acquisitionOrderId } = get();

      if (!isTnCAccepted) {
        throw new Error('Terms and Condition is required');
      }

      if (transactionType === BondTransactionActionEnum.BUY) {
        const { buy } = useBondPreviewStore.getState();
        await postBondBuy({
          price_rate: buy.price_rate,
          product_id: buy.product.id,
          unit: parseInt(buy.unit, 10),
        });
      }

      if (transactionType === BondTransactionActionEnum.SELL) {
        const { sell } = useBondPreviewStore.getState();
        await postBondSell({
          product_id: sell.product.id,
          buy_order_id: acquisitionOrderId,
          unit: sell.units,
          price_rate: sell.price.sell,
        });
      }
      // inform the ui layer that the transaction is success
      set({ isLoading: false, isSuccess: true });

      // perform cleanup
      const { clearData: clearTransaction } = get();
      clearTransaction(); // transaction states
    } catch (error) {
      set({ isLoading: false, isSuccess: false });
      Promise.reject(error);
    }
  },

  clearData() {
    set(initialState);
  },

  setTnC(value) {
    set({ isTnCAccepted: value });
  },

  async initiateBuyTransaction(productId) {
    const { loadBuyPreview } = useBondPreviewStore.getState();
    const { loadBalance } = useCashOnHandStore.getState();

    set({ productId, transactionType: BondTransactionActionEnum.BUY });

    await Promise.all([loadBalance(), loadBuyPreview(productId)]);
  },

  async initiateSellTransaction(productId, acquisitionOrderId) {
    const { loadSellPreview } = useBondPreviewStore.getState();
    const { loadBalance } = useCashOnHandStore.getState();

    set({
      acquisitionOrderId,
      productId,
      transactionType: BondTransactionActionEnum.SELL,
    });

    await Promise.all([
      loadBalance(),
      loadSellPreview(productId, acquisitionOrderId, 1),
    ]);
  },
}));
